import {
  BasePayInputTypeV3EnumDto,
  IcLookupPositionPayDto,
} from '@data-access/bulk-operations-api';

export function calculateSalariesUsingIcLookupPositionPayDto(
  pay: IcLookupPositionPayDto,
  normalWeeklyHours: string | undefined,
): IcLookupPositionPayDto {
  const hourlyWage = parseFloat(pay.hourlyWage ?? '');
  const fullTimeMonthlySalary = parseFloat(pay.fullTimeMonthlySalary ?? '');
  const fullTimeYearlySalary = parseFloat(pay.fullTimeYearlySalary ?? '');
  const workHours = parseFloat(normalWeeklyHours ?? '');

  if (!pay.inputType || !workHours) {
    return pay;
  }

  const computedSalaries = calculateSalariesInternal(
    pay.inputType,
    hourlyWage,
    fullTimeMonthlySalary,
    fullTimeYearlySalary,
    workHours,
  );

  return new IcLookupPositionPayDto({
    ...pay,
    hourlyWage: toFormatedString(computedSalaries.hourlyWage),
    fullTimeMonthlySalary: toFormatedString(computedSalaries.fullTimeMonthlySalary),
    fullTimeYearlySalary: toFormatedString(computedSalaries.fullTimeYearlySalary),
  });
}

export function calculateAllSalaryValues(
  inputType: BasePayInputTypeV3EnumDto,
  hourlyWage: string | number | null | undefined,
  fullTimeMonthlySalary: string | number | null | undefined,
  fullTimeYearlySalary: string | number | null | undefined,
  normalWeeklyHours: string | number | null | undefined,
): CalculatedSalaryValues {
  if (checkForNullOrEmptyStringValue(normalWeeklyHours)) {
    return {};
  }

  const workHours = Number(normalWeeklyHours);

  if (isNaN(workHours) || workHours === 0) {
    return {};
  }

  const hourly = toNumberOrNaN(hourlyWage);
  const monthly = toNumberOrNaN(fullTimeMonthlySalary);
  const yearly = toNumberOrNaN(fullTimeYearlySalary);

  const computed = calculateSalariesInternal(inputType, hourly, monthly, yearly, workHours);

  return {
    hourlyWage: toFormatedString(computed.hourlyWage),
    fullTimeMonthlySalary: toFormatedString(computed.fullTimeMonthlySalary),
    fullTimeYearlySalary: toFormatedString(computed.fullTimeYearlySalary),
  };
}

function calculateSalariesInternal(
  inputType: BasePayInputTypeV3EnumDto,
  hourlyWage: number,
  fullTimeMonthlySalary: number,
  fullTimeYearlySalary: number,
  workHours: number,
): ComputedSalaries {
  switch (inputType) {
    case BasePayInputTypeV3EnumDto.Hourly:
      if (isNaN(hourlyWage)) break;

      // `hourlyWage * (WTA.normalWeeklyHours * 52)`
      fullTimeYearlySalary = hourlyWage * (workHours * 52);
      // `hourlyWage * (WTA.normalWeeklyHours * 52 / 12)`
      fullTimeMonthlySalary = hourlyWage * ((workHours * 52) / 12);
      break;
    case BasePayInputTypeV3EnumDto.FullTimeMonthly:
      if (isNaN(fullTimeMonthlySalary)) break;

      // `fullTimeMonthlySalary * 12`
      fullTimeYearlySalary = fullTimeMonthlySalary * 12;
      // `(fullTimeMonthlySalary * 12) / (WTA.normalWeeklyHours * 52)`
      hourlyWage = (fullTimeMonthlySalary * 12) / (workHours * 52);
      break;
    case BasePayInputTypeV3EnumDto.FullTimeYearly:
      if (isNaN(fullTimeYearlySalary)) break;

      // `fullTimeYearlySalary / 12`
      fullTimeMonthlySalary = fullTimeYearlySalary / 12;
      // `fullTimeYearlySalary / (WTA.normalWeeklyHours * 52)`
      hourlyWage = fullTimeYearlySalary / (workHours * 52);
      break;
  }

  return {
    inputType: inputType,
    hourlyWage: hourlyWage,
    fullTimeMonthlySalary: fullTimeMonthlySalary,
    fullTimeYearlySalary: fullTimeYearlySalary,
  };
}

function toFormatedString(input: number | undefined): string | undefined {
  return input !== undefined && !isNaN(input) ? input.toFixed(2).replace(/\.00$/, '') : undefined;
}

/**
 * Calculate the actual salary based on full-time salary and part-time factor (FTE percentage).
 * Formula: (fullTimeSalary / 100) * partTimeFactor
 * @returns The calculated salary as a formatted string, or undefined if inputs are invalid
 */
export function calculateActualSalary(
  fullTimeSalary: string | number | null | undefined,
  partTimeFactor: string | number | null | undefined,
): string | undefined {
  // Check for null, undefined, or empty string
  if (
    checkForNullOrEmptyStringValue(fullTimeSalary) ||
    checkForNullOrEmptyStringValue(partTimeFactor)
  ) {
    return undefined;
  }

  const salary = Number(fullTimeSalary);
  const percentage = Number(partTimeFactor);

  if (isNaN(salary) || isNaN(percentage)) {
    return undefined;
  }

  const result = (salary / 100) * percentage;
  return toFormatedString(result);
}

/**
 * Calculate the rate basis (hourly rate) from monthly salary and work time agreement hours.
 * Formula: (fullTimeMonthlySalary * 12) / (52 * wtaHours)
 * @returns The calculated rate basis as a formatted string, or undefined if inputs are invalid
 */
export function calculateRateBasis(
  monthlySalary: string | number | null | undefined,
  wtaHours: string | number | null | undefined,
): string | undefined {
  if (checkForNullOrEmptyStringValue(monthlySalary) || checkForNullOrEmptyStringValue(wtaHours)) {
    return undefined;
  }

  const salary = Number(monthlySalary);
  const hours = Number(wtaHours);

  if (isNaN(salary) || isNaN(hours) || hours === 0) {
    return undefined;
  }

  const result = (salary * 12) / (52 * hours);
  return toFormatedString(result);
}

/**
 * Calculate the actual hours per week based on normal weekly hours and FTE percentage.
 * Formula: (normalWeeklyHours * ftePercentage) / 100
 * @returns The calculated actual hours per week as a formatted string, or undefined if inputs are invalid
 */
export function calculateActualHoursPerWeek(
  normalWeeklyHours: string | number | null | undefined,
  ftePercentage: string | number | null | undefined,
): string | undefined {
  if (
    checkForNullOrEmptyStringValue(normalWeeklyHours) ||
    checkForNullOrEmptyStringValue(ftePercentage)
  ) {
    return undefined;
  }

  const hours = Number(normalWeeklyHours);
  const percentage = Number(ftePercentage);

  if (isNaN(hours) || isNaN(percentage)) {
    return undefined;
  }

  const result = (hours * percentage) / 100;
  return toFormatedString(result);
}

function checkForNullOrEmptyStringValue(value: string | number | null | undefined): boolean {
  return value == null || value === '';
}

function toNumberOrNaN(value: string | number | null | undefined): number {
  return value === '' ? NaN : Number(value);
}

type ComputedSalaries = {
  inputType: BasePayInputTypeV3EnumDto;
  hourlyWage: number;
  fullTimeMonthlySalary: number;
  fullTimeYearlySalary: number;
};

export type CalculatedSalaryValues = {
  hourlyWage?: string;
  fullTimeMonthlySalary?: string;
  fullTimeYearlySalary?: string;
};
